/**
 * Posts 以外のユーザー向けページのパス・表示タイトルを一元管理する。
 * アクセス解析のページ列表示・ウィジェット・リンク生成で共通利用する。
 */

import type { Category } from '../types/header';
import type { Tag } from '../api/tags';

/** 言語 */
export type DisplayLang = 'ja' | 'en';

/** 既知のユーザー向けページ（パスセグメント＝言語プレフィックス除く）と表示名 */
export const USER_PAGE_ROUTES = {
  home: { path: '', titleJa: 'ホーム', titleEn: 'Home' },
  about: { path: 'about', titleJa: 'About', titleEn: 'About' },
  termsOfService: { path: 'terms-of-service', titleJa: '利用規約', titleEn: 'Terms of Service' },
  privacyPolicy: { path: 'privacy-policy', titleJa: 'プライバシーポリシー', titleEn: 'Privacy Policy' },
  apps: { path: 'apps', titleJa: 'アプリ', titleEn: 'Apps' },
  appsAnalytics: { path: 'apps/analytics', titleJa: 'アクセス履歴', titleEn: 'Access History' },
  appsCidrPlanner: { path: 'apps/cidr-planner', titleJa: 'CIDRプランナー', titleEn: 'CIDR Planner' },
  appsMyNazca: { path: 'apps/mynazca', titleJa: 'MyNazca(α版)', titleEn: 'MyNazca (alpha)' },
  appsMyNazcaHelpUsage: { path: 'apps/mynazca/help/usage', titleJa: '使い方', titleEn: 'How to use' },
  appsMyNazcaHelpRouteTips: {
    path: 'apps/mynazca/help/route-tips',
    titleJa: 'ルート作成のコツ',
    titleEn: 'Route creation tips',
  },
  appsMyNazcaHelpContribute: {
    path: 'apps/mynazca/help/contribute',
    titleJa: '開発協力のお願い',
    titleEn: 'Help us improve MyNazca',
  },
} as const;

/** カテゴリー・タグのプレフィックス（表示用） */
const CATEGORY_PREFIX = { ja: 'カテゴリー: ', en: 'Category: ' } as const;
const TAG_PREFIX = { ja: 'タグ: ', en: 'Tag: ' } as const;

/**
 * page_path から言語プレフィックスを除いたパス部分とクエリを取得する
 */
function parsePagePath(pagePath: string): { pathOnly: string; withoutLang: string; lastSegment: string; search: string } {
  const trimmed = pagePath.trim();
  const qIdx = trimmed.indexOf('?');
  const pathOnly = (qIdx !== -1 ? trimmed.slice(0, qIdx) : trimmed).replace(/\/+$/, '');
  const search = qIdx !== -1 ? trimmed.slice(qIdx) : '';
  const withoutLang = pathOnly.replace(/^\/(ja|en)(\/|$)/, '$2').replace(/^\/+/, '').replace(/\/+$/, '') || '';
  const lastSegment = withoutLang.split('/').filter(Boolean).pop() ?? '';
  return { pathOnly, withoutLang, lastSegment, search };
}

/**
 * スラッグを表示用文字列に整形する（一覧にない場合のフォールバック）
 */
export function slugToDisplayString(slug: string): string {
  return slug
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * 既知のパス（利用規約・about・ホーム・apps 等）を表示用タイトルに変換する。
 * 記事（posts）のパスは null を返す（呼び出し元で API title 等を使用すること）。
 */
export function getDisplayTitleForPath(
  pagePath: string | undefined,
  lang: DisplayLang,
): string | null {
  if (!pagePath || typeof pagePath !== 'string') return null;
  const { withoutLang, lastSegment, search } = parsePagePath(pagePath);

  if (withoutLang === USER_PAGE_ROUTES.termsOfService.path || lastSegment === USER_PAGE_ROUTES.termsOfService.path) {
    return lang === 'ja' ? USER_PAGE_ROUTES.termsOfService.titleJa : USER_PAGE_ROUTES.termsOfService.titleEn;
  }
  if (withoutLang === USER_PAGE_ROUTES.privacyPolicy.path || lastSegment === USER_PAGE_ROUTES.privacyPolicy.path) {
    return lang === 'ja' ? USER_PAGE_ROUTES.privacyPolicy.titleJa : USER_PAGE_ROUTES.privacyPolicy.titleEn;
  }
  if (withoutLang === USER_PAGE_ROUTES.about.path || lastSegment === USER_PAGE_ROUTES.about.path) {
    return lang === 'ja' ? USER_PAGE_ROUTES.about.titleJa : USER_PAGE_ROUTES.about.titleEn;
  }
  if (withoutLang === '' || withoutLang === '/') {
    const categoryMatch = search && /[?&]category=([^&?#]+)/.exec(search);
    if (categoryMatch) {
      const slug = decodeURIComponent(categoryMatch[1].trim());
      return `${CATEGORY_PREFIX[lang]}${slug}`;
    }
    const tagMatch = search && /[?&]tag=([^&?#]+)/.exec(search);
    if (tagMatch) {
      const slug = decodeURIComponent(tagMatch[1].trim());
      return `${TAG_PREFIX[lang]}${slug}`;
    }
    return lang === 'ja' ? USER_PAGE_ROUTES.home.titleJa : USER_PAGE_ROUTES.home.titleEn;
  }
  if (withoutLang === USER_PAGE_ROUTES.appsCidrPlanner.path) {
    return lang === 'ja' ? USER_PAGE_ROUTES.appsCidrPlanner.titleJa : USER_PAGE_ROUTES.appsCidrPlanner.titleEn;
  }
  if (withoutLang === USER_PAGE_ROUTES.appsAnalytics.path) {
    return lang === 'ja' ? USER_PAGE_ROUTES.appsAnalytics.titleJa : USER_PAGE_ROUTES.appsAnalytics.titleEn;
  }
  if (withoutLang === USER_PAGE_ROUTES.appsMyNazca.path) {
    return lang === 'ja' ? USER_PAGE_ROUTES.appsMyNazca.titleJa : USER_PAGE_ROUTES.appsMyNazca.titleEn;
  }
  if (withoutLang === USER_PAGE_ROUTES.appsMyNazcaHelpUsage.path) {
    return lang === 'ja'
      ? USER_PAGE_ROUTES.appsMyNazcaHelpUsage.titleJa
      : USER_PAGE_ROUTES.appsMyNazcaHelpUsage.titleEn;
  }
  if (withoutLang === USER_PAGE_ROUTES.appsMyNazcaHelpRouteTips.path) {
    return lang === 'ja'
      ? USER_PAGE_ROUTES.appsMyNazcaHelpRouteTips.titleJa
      : USER_PAGE_ROUTES.appsMyNazcaHelpRouteTips.titleEn;
  }
  if (withoutLang === USER_PAGE_ROUTES.appsMyNazcaHelpContribute.path) {
    return lang === 'ja'
      ? USER_PAGE_ROUTES.appsMyNazcaHelpContribute.titleJa
      : USER_PAGE_ROUTES.appsMyNazcaHelpContribute.titleEn;
  }
  if (withoutLang === USER_PAGE_ROUTES.apps.path || lastSegment === USER_PAGE_ROUTES.apps.path) {
    return lang === 'ja' ? USER_PAGE_ROUTES.apps.titleJa : USER_PAGE_ROUTES.apps.titleEn;
  }
  return null;
}

/**
 * 「カテゴリー: slug」形式のタイトルを、表示言語のカテゴリー名に置き換える
 */
export function resolveCategoryDisplayTitle(
  fromPath: string | null,
  lang: DisplayLang,
  categories: Category[],
): string | null {
  if (!fromPath) return null;
  const prefix = CATEGORY_PREFIX[lang];
  if (!fromPath.startsWith(prefix)) return fromPath;
  const slug = fromPath.slice(prefix.length).trim();
  const cat = categories.find((c) => c.slug === slug);
  const name = cat
    ? (lang === 'ja' ? cat.name : (cat.nameEn ?? cat.name))
    : slugToDisplayString(slug);
  return `${prefix}${name}`;
}

/**
 * 「タグ: slug」形式のタイトルを、表示言語のタグ名に置き換える
 */
export function resolveTagDisplayTitle(
  fromPath: string | null,
  lang: DisplayLang,
  tags: Tag[],
): string | null {
  if (!fromPath) return null;
  const prefix = TAG_PREFIX[lang];
  if (!fromPath.startsWith(prefix)) return fromPath;
  const slug = fromPath.slice(prefix.length).trim();
  const tag = tags.find((t) => t.slug === slug);
  const name = tag
    ? (lang === 'ja' ? tag.name : (tag.nameEn ?? tag.name))
    : slugToDisplayString(slug);
  return `${prefix}${name}`;
}

/**
 * 指定パスが About ページかどうか（スラッグのみ・フルパス両対応）
 */
export function isAboutPage(pagePath: string | undefined): boolean {
  if (!pagePath || typeof pagePath !== 'string') return false;
  const pathOnly = pagePath.replace(/\?.*$/, '').replace(/#.*$/, '').replace(/\/+$/, '');
  return (
    pagePath.trim() === USER_PAGE_ROUTES.about.path ||
    pathOnly === USER_PAGE_ROUTES.about.path ||
    pathOnly.endsWith('/about')
  );
}

/**
 * 指定パスがアクセス履歴（apps/analytics）ページかどうか。
 * 言語を問わず true のときはリンクを出さずテキストのみ表示する（リンクなしに統一）。
 */
export function isAnalyticsPage(pagePath: string | undefined, _lang: DisplayLang): boolean {
  if (!pagePath || typeof pagePath !== 'string') return false;
  const pathOnly = pagePath.replace(/\?.*$/, '').replace(/#.*$/, '').replace(/\/+$/, '') || '';
  return pathOnly === '/ja/apps/analytics' || pathOnly === '/en/apps/analytics';
}

/**
 * 既知ページの言語付きパスを返す（リンク生成用）
 * segment: '' | 'about' | 'terms-of-service' | 'privacy-policy' | 'apps' | 'apps/analytics'
 */
export function getPagePathForLang(segment: string, lang: DisplayLang): string {
  if (segment === '') return `/${lang}`;
  return `/${lang}/${segment}`;
}
